// src/pages/marketplace/MarketplaceProductPage.tsx
// Full product detail/landing page with tabs: Overview, Reviews, Dependencies, Changelog.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Badge,
  Alert,
  ErrorAlert,
} from '@react/components';
import { StatusBadge } from '../../components/StatusBadge';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Render filled/empty stars for a given rating (0-5). */
function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClass = size === 'lg' ? 'w-6 h-6' : size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
  const full = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          className={`${sizeClass} ${i < full ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
        </svg>
      ))}
    </span>
  );
}

/** Format a number for display (e.g. 1200 => "1.2k"). */
function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

/** Map ProductStatus to StatusBadge variant. */
function statusVariant(status: string): string {
  switch (status) {
    case 'PRODUCT_STATUS_ACTIVE': return 'success';
    case 'PRODUCT_STATUS_BETA': return 'info';
    case 'PRODUCT_STATUS_DRAFT': return 'secondary';
    case 'PRODUCT_STATUS_MAINTENANCE': return 'warning';
    case 'PRODUCT_STATUS_DEPRECATED': return 'warning';
    case 'PRODUCT_STATUS_DISABLED': return 'error';
    default: return 'secondary';
  }
}

/** Human-readable status label. */
function statusLabel(status: string): string {
  return (status ?? '').replace('PRODUCT_STATUS_', '').replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

/** Map dependency requirement to badge variant. */
function depReqVariant(req: string): string {
  switch (req) {
    case 'PRODUCT_DEPENDENCY_REQUIREMENT_HARD': return 'error';
    case 'PRODUCT_DEPENDENCY_REQUIREMENT_SOFT': return 'warning';
    case 'PRODUCT_DEPENDENCY_REQUIREMENT_RECOMMENDED': return 'info';
    default: return 'secondary';
  }
}

function depReqLabel(req: string): string {
  return (req ?? '').replace('PRODUCT_DEPENDENCY_REQUIREMENT_', '').replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

type TabId = 'overview' | 'reviews' | 'dependencies' | 'changelog';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MarketplaceProductPage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();
  const { slug } = useParams<{ slug: string }>();

  const [product, setProduct] = useState<any>(null);
  const [marketplace, setMarketplace] = useState<any>(null);
  const [reviews, setReviews] = useState<any[]>([]);
  const [dependencies, setDependencies] = useState<any[]>([]);
  const [reverseDeps, setReverseDeps] = useState<any[]>([]);
  const [category, setCategory] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);

    const load = async () => {
      try {
        // Load product by slug
        const prod: any = (await store.GetProductBySlug(slug)) ?? store.currentItem;
        setProduct(prod);

        if (prod?.entity?.id) {
          // Parallel loads
          const [mkRes, revRes, depRes, rdepRes] = await Promise.allSettled([
            store.GetMarketplaceMetadata(prod.entity.id),
            store.ListProductReviews(prod.entity.id),
            store.ListProductDependencies(prod.entity.id),
            store.ListDependentProducts(prod.entity.id),
          ]);

          if (mkRes.status === 'fulfilled') {
            setMarketplace(mkRes.value?.marketplace ?? null);
          }
          if (revRes.status === 'fulfilled') {
            const reviewItems = revRes.value?.reviews?.items;
            setReviews(Array.isArray(reviewItems) ? reviewItems : []);
          }
          if (depRes.status === 'fulfilled' && depRes.value) {
            const depItems = depRes.value?.dependencies?.items;
            setDependencies(Array.isArray(depItems) ? depItems : []);
          }
          if (rdepRes.status === 'fulfilled' && rdepRes.value) {
            const rdepItems = rdepRes.value?.dependencies?.items;
            setReverseDeps(Array.isArray(rdepItems) ? rdepItems : []);
          }
        }

        // Load category info if present
        if (prod?.category_slug || prod?.category_id) {
          try {
            const catRes = await store.ListProductCategories();
            const catItems = catRes?.categories?.items;
            const cats = Array.isArray(catItems) ? catItems : [];
            const cat = cats.find(
              (c: any) => c.entity?.id === prod.category_id || c.slug === prod.category_slug,
            );
            setCategory(cat || null);
          } catch { /* ignore */ }
        }
      } catch {
        // Error handled by store
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [slug]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const avgRating = marketplace?.average_rating ?? 0;
  const reviewCount = marketplace?.review_count ?? reviews.length;
  const installCount = marketplace?.install_count ?? 0;
  const activeInstalls = marketplace?.active_installs ?? 0;
  const screenshotUrls: string[] = marketplace?.screenshot_urls ?? [];
  const longDescription = marketplace?.long_description ?? product?.description ?? '';
  const changelog = marketplace?.changelog ?? '';
  const version = marketplace?.version ?? '';
  const isVerified = marketplace?.is_verified ?? false;
  const tags: string[] = marketplace?.tags ?? [];
  const locales: string[] = marketplace?.supported_locales ?? [];
  const capabilities: string[] = product?.capabilities?.capabilities ?? [];

  // Rating breakdown (computed from loaded reviews)
  const ratingBreakdown = [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    count: reviews.filter((r: any) => r.rating === stars).length,
  }));
  const maxBreakdown = Math.max(...ratingBreakdown.map((b) => b.count), 1);

  // Dependency grouping
  const hardDeps = dependencies.filter(
    (d: any) => d.requirement === 'PRODUCT_DEPENDENCY_REQUIREMENT_HARD',
  );
  const softDeps = dependencies.filter(
    (d: any) =>
      d.requirement === 'PRODUCT_DEPENDENCY_REQUIREMENT_SOFT' ||
      d.requirement === 'PRODUCT_DEPENDENCY_REQUIREMENT_RECOMMENDED',
  );

  // Tab definitions
  const tabs: { id: TabId; label: string }[] = [
    { id: 'overview', label: t('marketplace.tabOverview') },
    { id: 'reviews', label: t('marketplace.tabReviews') + (reviewCount ? ` (${reviewCount})` : '') },
    { id: 'dependencies', label: t('marketplace.tabDependencies') },
    { id: 'changelog', label: t('marketplace.tabChangelog') },
  ];

  // ---------------------------------------------------------------------------
  // Loading / Error / Not Found
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    );
  }

  if (store.error) {
    return (
      <>
        <ErrorAlert error={store.error} />
        <Link to="/marketplace" className="mt-2 inline-block text-sm underline text-primary">
          {t('marketplace.backToMarketplace')}
        </Link>
      </>
    );
  }

  if (!product) {
    return (
      <Alert variant="warning" title={t('marketplace.productNotFound')}>
        <Link to="/marketplace" className="mt-2 inline-block text-sm underline text-primary">
          {t('marketplace.backToMarketplace')}
        </Link>
      </Alert>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/marketplace" className="text-muted-foreground hover:text-foreground">
          {t('marketplace.title')}
        </Link>
        {category && (
          <>
            <span className="mx-2 text-muted-foreground">/</span>
            <Link
              to={`/marketplace/categories/${category.slug}`}
              className="text-muted-foreground hover:text-foreground"
            >
              {category.name}
            </Link>
          </>
        )}
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{product.name}</span>
      </nav>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div className="flex items-start gap-5">
          {/* Icon */}
          {product.icon_url ? (
            <img
              src={product.icon_url}
              alt={product.name}
              className="w-16 h-16 rounded-xl object-cover shrink-0"
            />
          ) : (
            <div
              className="w-16 h-16 rounded-xl flex items-center justify-center text-white font-bold text-2xl shrink-0"
              style={{ backgroundColor: product.color_primary || '#6366f1' }}
            >
              {(product.name ?? '?')[0]?.toUpperCase()}
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-foreground">{product.name}</h1>
            <p className="text-muted-foreground mt-1">{product.tagline || product.description}</p>
            {product.owner?.display_name && (
              <p className="text-sm text-muted-foreground mt-1">
                {t('marketplace.by')} {product.owner.display_name}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <StatusBadge
            variant={statusVariant(product.status)}
            label={statusLabel(product.status)}
          />
          <Button size="lg">
            {t('marketplace.install')}
          </Button>
        </div>
      </div>

      {/* Main content layout: content + sidebar */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Content area */}
        <div className="flex-1 min-w-0">
          {/* Tab navigation */}
          <div className="border-b border-border mb-6">
            <div className="flex gap-0 -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab: Overview */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Screenshot gallery */}
              {screenshotUrls.length > 0 && (
                <div className="overflow-x-auto">
                  <div className="flex gap-4 pb-2" style={{ minWidth: 'max-content' }}>
                    {screenshotUrls.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt={`${product.name} screenshot ${i + 1}`}
                        className="h-56 rounded-lg object-cover shadow-sm border border-border"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Description */}
              {longDescription && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">
                    {t('marketplace.description')}
                  </h3>
                  <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-wrap">
                    {longDescription}
                  </div>
                </div>
              )}

              {/* Capabilities */}
              {capabilities.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">
                    {t('marketplace.capabilities')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {capabilities.map((cap: string) => {
                      const label = cap
                        .replace('PRODUCT_CAPABILITY_', '')
                        .replace(/_/g, ' ')
                        .toLowerCase()
                        .replace(/^\w/, (c) => c.toUpperCase());
                      return (
                        <Badge key={cap} variant="secondary">
                          {label}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Resource types */}
              {product.resource_types?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">
                    {t('marketplace.resourceTypes')}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {product.resource_types.map((rt: string) => (
                      <Badge key={rt} variant="default">
                        {rt}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tab: Reviews */}
          {activeTab === 'reviews' && (
            <div className="space-y-6">
              {/* Rating summary */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-8">
                    {/* Big average */}
                    <div className="text-center">
                      <div className="text-5xl font-bold text-foreground">{avgRating.toFixed(1)}</div>
                      <StarRating rating={avgRating} size="md" />
                      <p className="text-sm text-muted-foreground mt-1">
                        {reviewCount} {t('marketplace.reviews')}
                      </p>
                    </div>
                    {/* Breakdown bars */}
                    <div className="flex-1 space-y-2 w-full">
                      {ratingBreakdown.map(({ stars, count }) => (
                        <div key={stars} className="flex items-center gap-3">
                          <span className="text-sm text-muted-foreground w-4 text-right">{stars}</span>
                          <svg className="w-4 h-4 text-yellow-400 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
                          </svg>
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-yellow-400 rounded-full"
                              style={{ width: `${(count / maxBreakdown) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground w-8">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Write a review button */}
              <div className="flex justify-end">
                <Link to={`/marketplace/${slug}/reviews`}>
                  <Button variant="secondary">
                    {t('marketplace.writeReview')}
                  </Button>
                </Link>
              </div>

              {/* Review list */}
              {reviews.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground">
                      {t('marketplace.noReviews')}
                    </p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {reviews.map((review: any) => (
                    <Card key={review.entity?.id || Math.random()}>
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          {/* Author avatar placeholder */}
                          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-sm font-medium text-muted-foreground shrink-0">
                            {(review.author?.display_name ?? 'A')[0]?.toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium text-foreground">
                                {review.author?.display_name || t('marketplace.anonymous')}
                              </span>
                              <StarRating rating={review.rating ?? 0} />
                              <span className="text-xs text-muted-foreground">
                                {review.created_at
                                  ? new Date(review.created_at).toLocaleDateString()
                                  : ''}
                              </span>
                            </div>
                            {review.title && (
                              <h4 className="text-sm font-semibold text-foreground mb-1">
                                {review.title}
                              </h4>
                            )}
                            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                              {review.body}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Link to full reviews page */}
              {reviews.length > 5 && (
                <div className="text-center">
                  <Link to={`/marketplace/${slug}/reviews`}>
                    <Button variant="secondary">
                      {t('marketplace.viewAllReviews')}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          )}

          {/* Tab: Dependencies */}
          {activeTab === 'dependencies' && (
            <div className="space-y-6">
              {/* Required (Hard) */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('marketplace.requiredProducts')}
                </h3>
                {hardDeps.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('marketplace.noRequiredDeps')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {hardDeps.map((dep: any) => (
                      <Card key={dep.entity?.id || Math.random()}>
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <Link
                              to={`/marketplace/${dep.required_product?.slug || dep.required_product?.id}`}
                              className="text-sm font-medium text-primary hover:underline"
                            >
                              {dep.required_product?.name || dep.required_product?.id || t('marketplace.unknownProduct')}
                            </Link>
                            {dep.description && (
                              <p className="text-xs text-muted-foreground mt-0.5">{dep.description}</p>
                            )}
                          </div>
                          <Badge variant="error" size="sm">{depReqLabel(dep.requirement)}</Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Recommended (Soft) */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('marketplace.recommendedProducts')}
                </h3>
                {softDeps.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('marketplace.noRecommendedDeps')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {softDeps.map((dep: any) => (
                      <Card key={dep.entity?.id || Math.random()}>
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <Link
                              to={`/marketplace/${dep.required_product?.slug || dep.required_product?.id}`}
                              className="text-sm font-medium text-primary hover:underline"
                            >
                              {dep.required_product?.name || dep.required_product?.id || t('marketplace.unknownProduct')}
                            </Link>
                            {dep.description && (
                              <p className="text-xs text-muted-foreground mt-0.5">{dep.description}</p>
                            )}
                          </div>
                          <Badge variant={depReqVariant(dep.requirement) as any} size="sm">
                            {depReqLabel(dep.requirement)}
                          </Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>

              {/* Reverse deps */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">
                  {t('marketplace.dependentProducts')}
                </h3>
                {reverseDeps.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {t('marketplace.noDependents')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {reverseDeps.map((dep: any) => (
                      <Card key={dep.entity?.id || Math.random()}>
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <Link
                              to={`/marketplace/${dep.dependent_product?.slug || dep.dependent_product?.id}`}
                              className="text-sm font-medium text-primary hover:underline"
                            >
                              {dep.dependent_product?.name || dep.dependent_product?.id || t('marketplace.unknownProduct')}
                            </Link>
                            {dep.description && (
                              <p className="text-xs text-muted-foreground mt-0.5">{dep.description}</p>
                            )}
                          </div>
                          <Badge variant={depReqVariant(dep.requirement) as any} size="sm">
                            {depReqLabel(dep.requirement)}
                          </Badge>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab: Changelog */}
          {activeTab === 'changelog' && (
            <div className="space-y-4">
              {version && (
                <div className="flex items-center gap-3">
                  <Badge variant="primary">{t('marketplace.version')} {version}</Badge>
                </div>
              )}
              {changelog ? (
                <Card>
                  <CardContent className="p-6">
                    <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-wrap">
                      {changelog}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground">
                      {t('marketplace.noChangelog')}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <aside className="w-full lg:w-72 shrink-0">
          <Card>
            <CardContent className="p-5 space-y-5">
              {/* Install counts */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {t('marketplace.installs')}
                  </p>
                  <p className="text-lg font-semibold text-foreground">{formatCount(installCount)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">
                    {t('marketplace.activeInstalls')}
                  </p>
                  <p className="text-lg font-semibold text-foreground">{formatCount(activeInstalls)}</p>
                </div>
              </div>

              {/* Rating */}
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  {t('marketplace.rating')}
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-semibold text-foreground">{avgRating.toFixed(1)}</span>
                  <StarRating rating={avgRating} />
                  <span className="text-xs text-muted-foreground">({reviewCount})</span>
                </div>
              </div>

              {/* Version */}
              {version && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    {t('marketplace.version')}
                  </p>
                  <p className="text-sm text-foreground">{version}</p>
                </div>
              )}

              {/* Verified */}
              {isVerified && (
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="text-sm font-medium text-blue-600">
                    {t('marketplace.verified')}
                  </span>
                </div>
              )}

              {/* Supported locales */}
              {locales.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                    {t('marketplace.locales')}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {locales.map((locale) => (
                      <Badge key={locale} variant="secondary" size="sm">{locale}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Tags */}
              {tags.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
                    {t('marketplace.tags')}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {tags.map((tag) => (
                      <Badge key={tag} variant="default" size="sm">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Links */}
              <div className="space-y-2 pt-2 border-t border-border">
                {product.docs_url && (
                  <a
                    href={product.docs_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    {t('marketplace.documentation')}
                  </a>
                )}
                {product.support_url && (
                  <a
                    href={product.support_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                    {t('marketplace.support')}
                  </a>
                )}
                {product.web_url && (
                  <a
                    href={product.web_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    {t('marketplace.website')}
                  </a>
                )}
              </div>

              {/* Report link */}
              <div className="pt-2 border-t border-border">
                <button className="text-xs text-muted-foreground hover:text-destructive transition-colors">
                  {t('marketplace.report')}
                </button>
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
});

export default MarketplaceProductPage;
